import { useEffect, useState } from "react";
import { Apple, Play, QrCode, ArrowUpRight, Smartphone } from "lucide-react";
import { DOWNLOAD } from "@/constants/testIds";
import { useContent } from "@/context/ContentContext";

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
};

const QRPlaceholder = ({ url }) => (
  <img
    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(url)}&color=16241B&bgcolor=FBFAF6&margin=0`}
    alt="QR code de téléchargement Zafara"
    className="h-40 w-40 rounded-2xl border border-[#EDE7D9] bg-white p-3"
    loading="lazy"
  />
);

export const DownloadCTA = () => {
  const { content } = useContent();
  const APP_STORE_URL = content.stores.appStore;
  const PLAY_STORE_URL = content.stores.playStore;

  const [platform, setPlatform] = useState("desktop");

  useEffect(() => {
    setPlatform(detectPlatform());
  }, []);

  const smartUrl =
    platform === "ios"
      ? APP_STORE_URL
      : platform === "android"
      ? PLAY_STORE_URL
      : APP_STORE_URL;

  // Smart link for QR code: server-side detects the scanning device's UA
  // and 302-redirects to the correct store. Encoded ONCE in the QR so any
  // phone that scans lands on the right store.
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : process.env.REACT_APP_BACKEND_URL || "";
  const qrSmartLink = `${origin}/api/download`;

  return (
    <section
      id="download"
      data-testid={DOWNLOAD.root}
      className="relative py-24 md:py-32 bg-[#0F2A1B] text-white noise-overlay overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-[#F5A800]/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-24 h-96 w-96 rounded-full bg-[#0E7A3B]/40 blur-3xl" />
      <div className="market-frieze absolute top-0 left-0 right-0" />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="font-utility text-xs uppercase tracking-[0.25em] text-[#F5A800]">
              Téléchargez l'app
            </p>
            <h2 className="mt-5 font-display text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tighter">
              Emportez le Bénin
              <br />
              <span className="text-[#F5A800]">partout avec vous.</span>
            </h2>
            <p className="mt-6 text-lg text-white/75 max-w-xl leading-relaxed">
              Zafara est disponible sur iOS et Android. Gratuit, léger, prêt en
              une minute.
            </p>

            {(platform === "ios" || platform === "android") && (
              <a
                data-testid={DOWNLOAD.smart}
                href={smartUrl}
                className="mt-10 inline-flex items-center gap-3 rounded-2xl bg-[#F5A800] text-[#16241B] px-7 py-4 text-lg font-display font-bold shadow-[0_15px_40px_rgba(245,168,0,0.35)] hover:-translate-y-0.5 hover:bg-white transition-all duration-300"
              >
                <Smartphone className="h-6 w-6" />
                {platform === "ios" ? "Ouvrir dans l'App Store" : "Ouvrir dans Google Play"}
                <ArrowUpRight className="h-5 w-5" />
              </a>
            )}

            <div className="mt-8 flex flex-col sm:flex-row items-start gap-3">
              <a
                data-testid={DOWNLOAD.appStore}
                href={APP_STORE_URL}
                className={`group inline-flex items-center gap-3 rounded-2xl bg-white text-[#16241B] px-5 py-4 min-w-[220px] shadow-[0_15px_40px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 transition-all duration-300 ${
                  platform === "android" ? "opacity-90" : ""
                }`}
              >
                <Apple className="h-7 w-7" />
                <div className="text-left">
                  <p className="font-utility text-[10px] uppercase tracking-widest opacity-60">
                    Télécharger sur
                  </p>
                  <p className="font-display font-bold text-lg leading-tight">
                    App Store
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 ml-auto opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>

              <a
                data-testid={DOWNLOAD.playStore}
                href={PLAY_STORE_URL}
                className={`group inline-flex items-center gap-3 rounded-2xl bg-[#0E7A3B] text-white px-5 py-4 min-w-[220px] shadow-[0_15px_40px_rgba(14,122,59,0.4)] hover:-translate-y-0.5 hover:bg-[#0d6a34] transition-all duration-300 ${
                  platform === "ios" ? "opacity-90" : ""
                }`}
              >
                <Play className="h-7 w-7 fill-white" />
                <div className="text-left">
                  <p className="font-utility text-[10px] uppercase tracking-widest opacity-80">
                    Disponible sur
                  </p>
                  <p className="font-display font-bold text-lg leading-tight">
                    Google Play
                  </p>
                </div>
                <ArrowUpRight className="h-4 w-4 ml-auto opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>

            <p className="mt-6 font-utility text-xs uppercase tracking-widest text-white/50">
              Compatible iOS 14+ · Android 8+ · Gratuit
            </p>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <div className="rounded-3xl bg-white/5 backdrop-blur border border-white/10 p-8 max-w-xs text-center">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[10px] font-utility uppercase tracking-widest text-[#F5A800]">
                <QrCode className="h-3 w-3" />
                Sur ordinateur ?
              </div>
              <p className="mt-4 font-display text-lg leading-tight">
                Scannez pour installer Zafara sur votre téléphone.
              </p>
              <div className="mt-6 flex justify-center">
                <QRPlaceholder url={qrSmartLink} />
              </div>
              <p className="mt-5 font-utility text-[10px] uppercase tracking-widest text-white/50">
                Redirige selon votre appareil
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="market-frieze absolute bottom-0 left-0 right-0" />
    </section>
  );
};
